import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { loginRequest } from './authConfig';
import ParkingTable from './components/ParkingTable';
import DefaultOwnerParkingTable from './components/DefaultOwnerParkingTable';
import ParkingSlotManager from './components/ParkingSlotManager';
import NotificationBlock from './components/NotificationBlock';
import './App.css';

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [powerUserMode, setPowerUserMode] = useState(false);
  const [currentView, setCurrentView] = useState('reservations'); // 'reservations' or 'slots'
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Reset to reservations view when PowerUser mode is turned off
  useEffect(() => {
    if (!powerUserMode && currentView === 'slots') {
      setCurrentView('reservations');
    }
  }, [powerUserMode, currentView]);

  // Close dialog on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && instructionsDialogOpen) {
        setInstructionsDialogOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [instructionsDialogOpen]);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      // Check if user has PowerUser role
      const roles = account.idTokenClaims?.roles || [];
      const isPowerUser = roles.includes('PowerUser');

      setUser({
        name: account.name,
        user_id: account.idTokenClaims?.email.toLowerCase() || account.username?.toLowerCase() || account.username,
        family_name: account.idTokenClaims?.family_name || account.name?.split(' ').pop() || 'User',
        isPowerUser: isPowerUser,
        roles: roles,
      });
    }
  }, [isAuthenticated, accounts]);

  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  const handleLogout = () => {
    // App-only logout: clear all local storage without redirecting to Azure
    try {
      // Clear entire localStorage
      localStorage.clear();
      
      // Clear entire sessionStorage
      sessionStorage.clear();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    
    // Clear any additional app state
    setUser(null);
    setPowerUserMode(false);
    
    // Navigate to root to force MSAL re-initialization
    window.location.href = window.location.origin;
  };

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>{t('app.title')}</h1>
          <p>{t('auth.pleaseSignIn')}</p>
          <button onClick={handleLogin} className="login-button">
            {t('auth.signIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="user-info-row">
          <div className="user-info-item">
            <span>{t('auth.welcome')}, {user?.name}</span>
          </div>
          <div className="user-info-item">
            {!powerUserMode && (
              <button
                className="help-button"
                onClick={() => setInstructionsDialogOpen(true)}
                title={t('app.instructions.title')}
                aria-label={t('app.instructions.title')}
              >
                ?
              </button>
            )}
            <div className="language-switcher">
              <button
                onClick={() => changeLanguage('fi')}
                className={`lang-button ${i18n.language === 'fi' ? 'active' : ''}`}
                title="Suomi"
              >
                FI
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`lang-button ${i18n.language === 'en' ? 'active' : ''}`}
                title="English"
              >
                EN
              </button>
            </div>
            <button onClick={handleLogout} className="logout-button">
              {t('auth.signOut')}
            </button>
          </div>
        </div>
        <div className="title-row">
          <h1>{t('app.subtitle')}</h1>
          <div className="title-row-actions">

            {user?.isPowerUser && (
              <label className="power-user-toggle-header">
                <input
                  type="checkbox"
                  checked={powerUserMode}
                  onChange={(e) => setPowerUserMode(e.target.checked)}
                />
                <span className="toggle-slider-header"></span>
                <span className="toggle-label-header">{t('app.powerUser')}</span>
              </label>
            )}
          </div>
        </div>
      </header>
      {user?.isPowerUser && powerUserMode && (
        <nav className="app-nav">
          <button
            className={`nav-button ${currentView === 'reservations' ? 'active' : ''}`}
            onClick={() => setCurrentView('reservations')}
          >
            {t('app.reservations')}
          </button>
          <button
            className={`nav-button ${currentView === 'slots' ? 'active' : ''}`}
            onClick={() => setCurrentView('slots')}
          >
            {t('app.manageSlots')}
          </button>
        </nav>
      )}
      {instructionsDialogOpen && (
        <div className="dialog-overlay" onClick={() => setInstructionsDialogOpen(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>{t('app.instructions.title')}</h2>
              <button
                className="dialog-close"
                onClick={() => setInstructionsDialogOpen(false)}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>
            <div className="dialog-body">
              <div className="instructions-section">
                <h3>{t('app.instructions.regularUsers.title')}</h3>
                <ul>
                  <li>{t('app.instructions.regularUsers.item1')}</li>
                  <li>{t('app.instructions.regularUsers.item2')}</li>
                  <li>{t('app.instructions.regularUsers.item3')}</li>
                  <li>{t('app.instructions.regularUsers.item4')}</li>
                </ul>
              </div>
              <div className="instructions-section">
                <h3>{t('app.instructions.defaultOwners.title')}</h3>
                <ul>
                  <li>{t('app.instructions.defaultOwners.item1')}</li>
                  <li>{t('app.instructions.defaultOwners.item2')}</li>
                  <li>{t('app.instructions.defaultOwners.item3')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      <main className="app-main">
        {currentView === 'reservations' ? (
          <>
            <NotificationBlock user={user} powerUserMode={powerUserMode} msalInstance={instance} />
            <ParkingTable user={user} powerUserMode={powerUserMode} msalInstance={instance} />
            <DefaultOwnerParkingTable user={user} powerUserMode={powerUserMode} msalInstance={instance} />
          </>
        ) : (
          <ParkingSlotManager user={user} powerUserMode={powerUserMode} msalInstance={instance} />
        )}
      </main>
    </div>
  );
}

export default App;
